import * as React from 'react';
import styled, { css } from 'styled-components';
import { styledWithProps } from '../utils';
import { Frame } from '../data/Frame';
import BackgroundStyle from '../data/BackgroundStyle';
import { FrameType } from '../data/FrameType';
import { getStoreState } from 'state/Store';

interface ThumbProps {
  backgroundStyle: BackgroundStyle;
}
const Thumb = styledWithProps<ThumbProps>()(styled.img)`
  width: 100%;
  height: 100%;
  position: relative;
  display: block;

  background-image: url(/media/frames-bg/grid-${props => {
    switch (props.backgroundStyle) {
      case BackgroundStyle.TransparentLight:
        return 'dark';
      case BackgroundStyle.TransparentDark:
        return 'light';
      default:
        return 'medium';
    }
  }}.png);
  object-fit: cover;
`;

interface WrapperProps {
  type: FrameType;
  selectable: boolean;
  focused: boolean;
  selected: boolean;
}
const Wrapper = styledWithProps<WrapperProps>()(styled.div)`
  display: inline-block;
  width: 60px;
  height: 60px;
  .app--figma-plugin & { width: 40px; height: 40px; }
  margin-right: 6px;
  position: relative;
  border-radius: 6px;

  box-shadow: 0px 0px 0px 2px var(--menu-divider-color);
  overflow: hidden;

  img {
    display: block;
  }

  ${props =>
    props.selectable &&
    css`
      &:hover {
        box-shadow: 0px 0px 0px 2px var(--accent-color-alpha-50);
      }
    `};

  ${props =>
    props.selectable &&
    css`
      cursor: pointer;
      img {
        opacity: 0.3;
      }
      > * {
        cursor: pointer;
      }
    `};

  ${props =>
    props.focused &&
    css`
      img {
        opacity: 1;
      }
    `};

  ${props =>
    props.selected &&
    css`
      box-shadow: 0px 0px 0px 2px var(--accent-color);

      &:hover {
        box-shadow: 0px 0px 0px 2px var(--accent-color);
      }
    `};

  ${props =>
    [
      FrameType.Text,
      FrameType.Captions,
      FrameType.Shape,
      FrameType.SolidColor,
      FrameType.Gradient,
      FrameType.SoundWave,
      FrameType.AnimatedGIF,
      FrameType.Video
    ].includes(props.type) &&
    css`
      &::after {
        position: absolute;
        right: 0;
        bottom: 0;
        content: '';
        display: block;
        width: 20px;
        height: 20px;
        background-image: url(/media/icons/corner-${props.type}.svg);
      }
    `}

    .app--figma-plugin &
    {
      width: 40px;
      box-shadow: 0px 0px 0px 1px var(--menu-divider-color);
    }
`;

const RelativeDuration = styled.div`
  background: rgba(255, 255, 255, 0.5);
  color: black;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  span {
    display: block;
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    line-height: 20px;
    filter: url(#sliderBorder);
    width: 100%;
  }
`;

interface Props {
  data: Frame;
  focused: boolean;
  selected: boolean;
  isOver: boolean;
  onSelect?: (frame: Frame) => void;
  onEdit?: (frame: Frame) => void;
  width: number;
  isResizing: boolean;
}

interface State {
  thumbURL: string | undefined;
  backgroundStyle: BackgroundStyle;
}

export default class extends React.Component<Props, State> {
  state = {
    thumbURL: undefined,
    backgroundStyle: BackgroundStyle.Unknown
  };
  constructor(props: Props) {
    super(props);

    this.updateThumbnail();

    props.data.on('edited', this.updateThumbnail);
  }

  componentWillUnmount() {
    this.props.data.off('edited', this.updateThumbnail);
  }

  updateThumbnail = () => {
    this.props.data.getThumbnailURL().then((thumbURL: string) => {
      this.setState({ thumbURL });
    });

    this.props.data
      .getBackgroundStylePromise()
      .then((backgroundStyle: BackgroundStyle) => {
        this.setState({ backgroundStyle });
      });
  };

  didClick = () => {
    if (this.props.onSelect) {
      this.props.onSelect(this.props.data);
    }
  };

  didDoubleClick = () => {
    if (this.props.onEdit && this.props.data.isEditable) {
      this.props.onEdit(this.props.data);
    }
  };

  getRelativeDurationString = (): string => {
    const { relativeDuration } = this.props.data;
    let durationInSecs =
      (getStoreState().flyer.motionSource.getDuration() / 1000) *
      relativeDuration;
    durationInSecs = Math.round(durationInSecs * 10) / 10;
    return `${durationInSecs}s`;
  };

  render() {
    const { isResizing } = this.props;
    const { type } = this.props.data;
    return (
      <Wrapper
        type={type}
        focused={this.props.focused}
        selectable={this.props.onSelect !== undefined}
        selected={this.props.selected}
        onClick={this.didClick}
        onDoubleClick={this.didDoubleClick}
        style={{ width: `${this.props.width}px` }}
        className={this.props.selected ? 'frame-thumb--selected' : undefined}
      >
        <Thumb
          alt=""
          backgroundStyle={this.state.backgroundStyle}
          src={this.state.thumbURL}
          draggable={false}
        />
        {isResizing && (
          <RelativeDuration>
            <span>{this.getRelativeDurationString()}</span>
          </RelativeDuration>
        )}
      </Wrapper>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/FrameThumb.tsx